<template>
  <div class="file-manage-container">
    <!-- 搜索和操作区域 -->
    <el-card class="search-card">
      <div slot="header" class="flex justify-between">
        <span>文件管理</span>
      </div>

      <el-row :gutter="20">
        <!-- 文件名称搜索 -->
        <el-col :span="4">
          <el-input
            v-model.trim="searchForm.fileName"
            placeholder="请输入文件名称"
            clearable
            prefix-icon="el-icon-search"
          />
        </el-col>

        <!-- 新增：所属部门筛选 -->
        <el-col :span="4">
          <el-select
            v-model="searchForm.deptId"
            placeholder="请选择所属部门"
            clearable
            filterable
            style="width: 100%"
            :loading="deptLoading"
          >
            <el-option
              v-for="dept in deptList"
              :key="dept.deptId"
              :label="dept.deptName"
              :value="dept.deptId"
            />
          </el-select>
        </el-col>

        <!-- 文件类型筛选 -->
        <el-col :span="4">
          <el-select
            v-model="searchForm.fileType"
            placeholder="请选择文件类型"
            clearable
            style="width: 100%"
          >
            <el-option label="所有类型" value=""></el-option>
            <el-option label="文档" value="doc"></el-option>
            <el-option label="表格" value="xls"></el-option>
            <el-option label="演示文稿" value="ppt"></el-option>
            <el-option label="PDF" value="pdf"></el-option>
            <el-option label="图片" value="img"></el-option>
            <el-option label="其他" value="other"></el-option>
          </el-select>
        </el-col>

        <!-- 上传时间范围 -->
        <el-col :span="8">
          <el-date-picker
            v-model="searchForm.uploadTimeRange"
            type="daterange"
            range-separator="至"
            start-placeholder="开始时间"
            end-placeholder="结束时间"
            value-format="yyyy-MM-dd"
            style="width: 100%"
          />
        </el-col>
      </el-row>

      <!-- 上传按钮：点击打开上传弹窗 -->
      <el-row style="margin-top: 15px" :gutter="20">
        <el-col :span="8">
          <el-button type="primary" @click="handleSearch">查询</el-button>
          <el-button @click="handleReset">重置</el-button>
          <el-button type="success" @click="openUploadModal">
            <i class="el-icon-upload2"></i> 上传文件
          </el-button>
          <!-- 批量下载按钮 -->
          <el-button
            type="info"
            @click="batchDownloadFiles"
            :disabled="selectedFileList.length === 0"
            icon="el-icon-download"
          >
            批量下载
          </el-button>
        </el-col>
      </el-row>
    </el-card>

    <!-- 文件列表区域 -->
    <el-card class="table-card">
      <el-table
        v-loading="loading"
        :data="fileList"
        border
        style="width: 100%"
        @selection-change="handleSelectionChange"
        ref="fileTable"
      >
        <!-- 多选列 -->
        <el-table-column type="selection" width="55" />

        <el-table-column
          prop="fileName"
          label="文件名称"
          min-width="200"
          show-overflow-tooltip
        >
          <template slot-scope="scope">
            <el-link @click="downloadFile(scope.row)">{{
              scope.row.fileName
            }}</el-link>
          </template>
        </el-table-column>
        <el-table-column prop="fileType" label="文件类型" width="100">
          <template slot-scope="scope">
            {{ getFileTypeText(scope.row.fileType) }}
          </template>
        </el-table-column>
        <el-table-column
          prop="uploadUserName"
          label="上传人"
          width="100"
        ></el-table-column>
        <el-table-column prop="fileSize" label="文件大小(MB)" width="120">
          <template slot-scope="scope">
            {{ (Number(scope.row.fileSize) / 1024 / 1024).toFixed(2) }}
          </template>
        </el-table-column>
        <el-table-column prop="createTime" label="上传时间" width="180">
          <template slot-scope="scope">
            {{ formatDate(scope.row.createTime) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="180" fixed="right">
          <template slot-scope="scope">
            <el-button size="mini" type="text" @click="downloadFile(scope.row)">
              下载
            </el-button>
            <el-button
              size="mini"
              @click="handleDelete(scope.row)"
              type="danger"
            >
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <el-row
        style="
          margin-top: 15px;
          display: flex;
          justify-content: flex-end;
          align-items: center;
        "
      >
        <el-pagination
          :current-page="pagination.current"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="pagination.size"
          :total="pagination.total"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </el-row>
    </el-card>

    <!-- 上传文件弹窗 -->
    <el-dialog
      title="上传附件"
      :visible.sync="uploadModalVisible"
      width="500px"
      destroy-on-close
      :before-close="handleDialogClose"
    >
      <!-- 移除 drag 属性，仅保留点击上传 -->
      <el-upload
        class="upload-demo"
        action="/callComponent/upload/uploadFile/uploadImage"
        accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
        :limit="1"
        name="uploadFile"
        :data="uploadExtraParams"
        :file-list="fileListUpload"
        :on-success="handleUploadSuccess"
        :on-remove="handleRemove"
        :auto-upload="true"
        :on-exceed="handleExceed"
        :on-error="handleUploadError"
      >
        <i class="el-icon-upload"></i>
        <div class="el-upload__text">点击选择文件</div>
        <div class="el-upload__tip" slot="tip">
          支持 PDF, Word, Excel, PPT (单个文件最大 10MB)
        </div>
      </el-upload>
      <div slot="footer" class="dialog-footer">
        <el-button @click="uploadModalVisible = false">取消</el-button>
        <el-button
          type="primary"
          @click="handleConfirmUpload"
          :loading="uploadConfirmLoading"
        >
          确定
        </el-button>
      </div>
    </el-dialog>

    <!-- 确认删除弹窗 -->
    <el-dialog
      title="确认删除"
      :visible.sync="deleteModalVisible"
      width="400px"
      :close-on-click-modal="false"
    >
      <div>确定要删除该文件吗？</div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="deleteModalVisible = false">取消</el-button>
        <el-button
          type="danger"
          @click="confirmDelete"
          :loading="deleteLoading"
        >
          确认删除
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  getFileList,
  uploadFile,
  deleteFile,
  listOrgTree,
} from "@/api/contract/fileManageApi";

export default {
  name: "FileManage",
  data() {
    return {
      // 加载状态
      loading: false,
      deleteLoading: false,
      uploadConfirmLoading: false,
      batchDownloadLoading: false,
      deptLoading: false,
      deptList: [],

      // 搜索表单
      searchForm: {
        fileName: "",
        fileType: "",
        uploadTimeRange: [],
        deptId: "",
      },

      // 上传相关
      uploadModalVisible: false,
      uploadExtraParams: {
        communityId: "", // 园区ID
      },
      fileListUpload: [], // 上传组件的文件列表
      uploadFileInfo: {}, // 存储上传成功后的完整文件信息
      currentUploadFileName: "", // 存储当前上传文件的原始名称
      currentUploadFileSize: 0, // 存储当前上传文件的大小

      // 表格数据
      fileList: [],
      selectedFileList: [], // 选中的文件列表

      // 分页配置
      pagination: {
        current: 1,
        size: 10,
        total: 0,
      },

      // 删除弹窗相关
      deleteModalVisible: false,
      currentDeleteId: "", // 当前要删除的文件ID（主键id）
    };
  },
  created() {
    this.uploadExtraParams.communityId = this.getCommunityId();
    this.refreshTree();
    this.fetchFileList();
  },
  methods: {
    // 获取园区ID
    getCommunityId() {
      return localStorage.getItem("communityId") || "";
    },

    // 加载部门树
    async refreshTree() {
      try {
        this.deptLoading = true;
        const communityId = this.uploadExtraParams.communityId;
        const params = { communityId };
        const response = await listOrgTree(params);
        if (response.code === 0) {
          const orgs = [response.data];
          this.deptList = this.extractDeptListFromOrgTree(orgs);
        } else {
          this.$message.error(response.msg || "加载部门列表失败");
        }
      } catch (error) {
        console.error("加载部门树异常：", error);
        this.$message.error("加载部门列表失败，请刷新重试");
      } finally {
        this.deptLoading = false;
      }
    },

    // 递归提取扁平部门数组
    extractDeptListFromOrgTree(orgTree) {
      let deptList = [];
      if (!orgTree || !Array.isArray(orgTree)) return deptList;
      orgTree.forEach((node) => {
        if (node.id) {
          deptList.push({
            deptId: node.id,
            deptName: node.text,
            allOrgName: node.allOrgName,
          });
        }
        if (node.children && node.children.length > 0) {
          const childDepts = this.extractDeptListFromOrgTree(node.children);
          deptList = [...deptList, ...childDepts];
        }
      });
      return deptList;
    },

    // ========== 基础工具方法 ==========
    // 格式化日期
    formatDate(dateStr) {
      if (!dateStr) return "-";
      let timestamp = Number(dateStr);
      if (isNaN(timestamp)) {
        timestamp = new Date(dateStr).getTime();
      }
      const date = new Date(timestamp);
      return `${date.getFullYear()}-${(date.getMonth() + 1)
        .toString()
        .padStart(2, "0")}-${date.getDate().toString().padStart(2, "0")} ${date
        .getHours()
        .toString()
        .padStart(2, "0")}:${date
        .getMinutes()
        .toString()
        .padStart(2, "0")}:${date.getSeconds().toString().padStart(2, "0")}`;
    },

    // 根据文件名获取文件类型
    getFileTypeByFileName(fileName) {
      if (!fileName) return "other";
      const ext = fileName.split(".").pop().toLowerCase();
      switch (ext) {
        case "doc":
        case "docx":
          return "doc";
        case "xls":
        case "xlsx":
          return "xls";
        case "ppt":
        case "pptx":
          return "ppt";
        case "pdf":
          return "pdf";
        case "jpg":
        case "png":
        case "jpeg":
          return "img";
        default:
          return "other";
      }
    },

    // 获取文件类型文本
    getFileTypeText(type) {
      const typeMap = {
        doc: "文档",
        xls: "表格",
        ppt: "演示文稿",
        pdf: "PDF",
        img: "图片",
        other: "其他",
      };
      return typeMap[type] || "未知";
    },

    // ========== 多选相关 ==========
    handleSelectionChange(val) {
      this.selectedFileList = val;
    },

    // ========== 数据获取 ==========
    async fetchFileList() {
      try {
        this.loading = true;
        const params = {
          page: this.pagination.current,
          row: this.pagination.size,
          fileName: this.searchForm.fileName,
          fileType: this.searchForm.fileType,
          communityId: this.uploadExtraParams.communityId,
          deptId: this.searchForm.deptId || "",
          startDate: this.searchForm.uploadTimeRange[0] || "",
          endDate: this.searchForm.uploadTimeRange[1] || "",
          isDeleted: "0",
        };
        const res = await getFileList(params);
        if (res.code === 0) {
          this.fileList = res.data || [];
          this.pagination.total = res.total || 0;
        } else {
          this.$message.error(res.msg || "获取文件列表失败");
        }
      } catch (error) {
        console.error("获取文件列表失败：", error);
        this.$message.error("获取文件列表失败，请重试");
      } finally {
        this.loading = false;
      }
    },

    // ========== 搜索和重置 ==========
    handleSearch() {
      this.pagination.current = 1;
      this.fetchFileList();
    },

    handleReset() {
      this.searchForm = {
        fileName: "",
        fileType: "",
        uploadTimeRange: [],
        deptId: "",
      };
      this.$refs.fileTable.clearSelection();
      this.selectedFileList = [];
      this.handleSearch();
    },

    // ========== 分页处理 ==========
    handleSizeChange(val) {
      this.pagination.size = val;
      this.$refs.fileTable.clearSelection();
      this.selectedFileList = [];
      this.fetchFileList();
    },

    handleCurrentChange(val) {
      this.pagination.current = val;
      this.$refs.fileTable.clearSelection();
      this.selectedFileList = [];
      this.fetchFileList();
    },

    // ========== 文件上传相关 ==========
    openUploadModal() {
      this.fileListUpload = [];
      this.uploadFileInfo = {};
      this.currentUploadFileName = "";
      this.currentUploadFileSize = 0;
      this.uploadModalVisible = true;
    },

    handleUploadSuccess(response, file) {
      try {
        this.currentUploadFileName = file.name;
        this.currentUploadFileSize = file.size;
        let resData = response;
        if (typeof response === "string") {
          resData = JSON.parse(response);
        }
        if (resData.url && resData.fileId) {
          this.uploadFileInfo = {
            fileName: file.name,
            fileType: this.getFileTypeByFileName(file.name),
            fileUrl: resData.url,
            fileId: resData.fileId,
            fileSize: file.size.toString(),
            communityId: this.uploadExtraParams.communityId,
          };
          this.$message.success("文件上传成功");
        } else {
          throw new Error("上传返回数据不完整，缺少url或fileId");
        }
      } catch (error) {
        console.error("解析上传结果失败：", error);
        this.$message.error("文件上传成功，但解析返回数据失败");
        this.fileListUpload = [];
      }
    },

    handleUploadError(error) {
      console.error("文件上传失败：", error);
      this.$message.error("文件上传失败，请重试");
      this.fileListUpload = [];
    },

    handleRemove(file, fileList) {
      this.uploadFileInfo = {};
      this.currentUploadFileName = "";
      this.currentUploadFileSize = 0;
      this.fileListUpload = fileList;
    },

    handleExceed(files, fileList) {
      this.$message.warning(
        `当前限制上传1个文件，你选择了${files.length + fileList.length}个文件`,
      );
    },

    handleDialogClose() {
      this.fileListUpload = [];
      this.uploadFileInfo = {};
      this.currentUploadFileName = "";
      this.currentUploadFileSize = 0;
      this.uploadConfirmLoading = false;
    },

    async handleConfirmUpload() {
      try {
        if (Object.keys(this.uploadFileInfo).length === 0) {
          this.$message.warning("请先上传文件后再点击确定");
          return;
        }
        this.uploadConfirmLoading = true;
        const res = await uploadFile(this.uploadFileInfo);
        if (res.code === 0) {
          this.$message.success("文件上传并保存成功");
          this.uploadModalVisible = false;
          this.fetchFileList();
          this.uploadFileInfo = {};
          this.currentUploadFileName = "";
          this.currentUploadFileSize = 0;
        } else {
          this.$message.error(res.msg || "文件信息保存失败");
        }
      } catch (error) {
        console.error("保存文件信息失败：", error);
        this.$message.error("文件信息保存失败，请重试");
      } finally {
        this.uploadConfirmLoading = false;
      }
    },

    // ========== 文件下载 ==========
    async downloadFile(row) {
      try {
        if (!row.fileUrl) {
          this.$message.error(`文件【${row.fileName}】地址不存在，无法下载`);
          return false;
        }
        const response = await fetch(row.fileUrl);
        if (!response.ok) {
          throw new Error(`下载失败：${response.statusText}`);
        }
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = row.fileName;
        link.style.display = "none";
        document.body.appendChild(link);
        link.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);
        this.$message.success(`文件【${row.fileName}】下载已开始`);
        return true;
      } catch (error) {
        console.error(`文件【${row.fileName}】下载失败：`, error);
        this.$message.error(
          `文件【${row.fileName}】下载失败：${error.message}`,
        );
        return false;
      }
    },

    async batchDownloadFiles() {
      if (this.selectedFileList.length === 0) {
        this.$message.warning("请先选择要下载的文件");
        return;
      }
      if (this.selectedFileList.length > 20) {
        this.$message.warning("单次最多支持下载20个文件，请减少选择数量");
        return;
      }
      this.$message.info(
        `开始批量下载${this.selectedFileList.length}个文件，请稍候...`,
      );
      for (let i = 0; i < this.selectedFileList.length; i++) {
        const row = this.selectedFileList[i];
        await this.downloadFile(row);
        const fileSizeMB = Number(row.fileSize) / 1024 / 1024;
        const delayTime = fileSizeMB > 5 ? 2000 : fileSizeMB > 2 ? 1000 : 500;
        await new Promise((resolve) => setTimeout(resolve, delayTime));
      }
      this.$message.success(
        `批量下载完成！共处理${this.selectedFileList.length}个文件`,
      );
      this.$refs.fileTable.clearSelection();
      this.selectedFileList = [];
    },

    // ========== 文件删除 ==========
    handleDelete(row) {
      this.currentDeleteId = row.id;
      this.deleteModalVisible = true;
    },

    async confirmDelete() {
      try {
        this.deleteLoading = true;
        const res = await deleteFile(this.currentDeleteId);
        if (res.code === 0) {
          this.$message.success("删除成功");
          this.deleteModalVisible = false;
          this.fetchFileList();
        } else {
          this.$message.error(res.msg || "删除失败");
        }
      } catch (error) {
        console.error("删除文件失败：", error);
        this.$message.error("删除文件失败，请重试");
      } finally {
        this.deleteLoading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.file-manage-container {
  padding: 20px;

  .search-card {
    margin-bottom: 20px;
  }

  .table-card {
    .el-table {
      --el-table-header-text-color: #333;
      --el-table-row-hover-bg-color: #f5f7fa;
    }

    .el-pagination {
      text-align: right;
    }
  }

  .dialog-footer {
    text-align: right;
  }

  .upload-demo {
    margin: 20px 0;
  }
}
</style>
